<template>
  <div class="min-h-screen pb-10">
    <!-- En-tête avec bouton de retour -->
    <div class="flex items-center justify-between mb-6">
      <BaseButton variant="ghost" @click="goBack">
        <template #icon-left>
          <i class="fa-solid fa-arrow-left"></i>
        </template>
        Retour
      </BaseButton>
      <div class="flex items-center gap-3">
        <!-- Bouton ajouter paiement -->
        <BaseButton
          v-if="invoice.id && invoice.status !== 'paid'"
          variant="primary"
          size="sm"
          @click="openPaymentModal"
        >
          <template #icon-left>
            <i class="fa-solid fa-plus"></i>
          </template>
          Ajouter un paiement
        </BaseButton>

        <!-- Dropdown de téléchargement -->
        <div class="relative invoice-download-dropdown-container">
          <BaseButton
            variant="outline-primary"
            size="sm"
            @click="toggleDownloadDropdown"
            :loading="isDownloading || isDownloadingDetailed"
            :class="{ 'active': showDownloadDropdown }"
          >
            <i v-if="isDownloading || isDownloadingDetailed" class="fa-solid fa-spinner fa-spin me-2"></i>
            <i v-else class="fa-solid fa-download me-2"></i>
            Télécharger
            <i class="fa-solid fa-chevron-down ms-2" :class="{ 'rotated': showDownloadDropdown }"></i>
          </BaseButton>
          <div v-if="showDownloadDropdown" class="invoice-download-dropdown">
            <button
              @click.stop="downloadInvoice('grouped')"
              :disabled="isDownloading || isDownloadingDetailed"
              class="dropdown-item"
            >
              <i class="fa-solid fa-file-pdf"></i>
              <span>Facture groupée</span>
            </button>
            <button
              @click.stop="downloadInvoice('detailed')"
              :disabled="isDownloading || isDownloadingDetailed"
              class="dropdown-item"
            >
              <i class="fa-solid fa-file-lines"></i>
              <span>Facture détaillée</span>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Loader -->
    <BaseCard v-if="loading">
      <div class="flex flex-col items-center justify-center py-20">
        <div class="w-10 h-10 border-4 border-gray-200 border-t-primary rounded-full animate-spin mb-4"></div>
        <p class="text-gray-600">Chargement...</p>
      </div>
    </BaseCard>

    <!-- Contenu principal -->
    <div v-else-if="invoice.id" class="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <!-- Colonne gauche: Infos -->
      <div class="lg:col-span-2 space-y-6">
        <!-- Carte principale -->
        <BaseCard>
          <template #header>
            <div class="flex items-start justify-between w-full">
              <div>
                <h2 class="text-2xl font-bold text-gray-900 mb-1">{{ invoice.invoice_number }}</h2>
                <p class="text-sm text-gray-600">Créée le {{ formatDate(invoice.created_at) }}</p>
              </div>
              <span :class="[
                'inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold uppercase',
                getStatusBadgeClass(invoice.status)
              ]">
                {{ getStatusLabel(invoice.status) }}
              </span>
            </div>
          </template>

          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div class="flex flex-col gap-1">
              <span class="text-xs font-medium text-gray-500 uppercase">Client</span>
              <div class="flex items-center gap-2">
                <span class="text-sm font-medium text-gray-900">{{ invoice.customer?.name || 'N/A' }}</span>
                <button
                  v-if="invoice.customer?.id"
                  @click="viewCustomer"
                  class="text-xs text-primary hover:text-primary-dark hover:underline transition-colors"
                  title="Voir le client"
                >
                  <i class="fa-solid fa-external-link"></i>
                </button>
              </div>
              <span v-if="invoice.customer?.phone" class="text-xs text-gray-500">{{ invoice.customer.phone }}</span>
            </div>
            <div class="flex flex-col gap-1">
              <span class="text-xs font-medium text-gray-500 uppercase">Date d'échéance</span>
              <span class="text-sm font-medium" :class="isOverdue ? 'text-red-600' : 'text-gray-900'">
                {{ invoice.due_date ? formatDateShort(invoice.due_date) : 'Non définie' }}
                <span v-if="isOverdue" class="text-xs ml-1">(En retard)</span>
              </span>
            </div>
            <div class="flex flex-col gap-1">
              <span class="text-xs font-medium text-gray-500 uppercase">Nombre de ventes</span>
              <span class="text-sm font-medium text-gray-900">{{ invoice.sales?.length || 0 }} vente(s)</span>
            </div>
            <div v-if="invoice.notes" class="flex flex-col gap-1 md:col-span-2">
              <span class="text-xs font-medium text-gray-500 uppercase">Notes</span>
              <span class="text-sm text-gray-700">{{ invoice.notes }}</span>
            </div>
          </div>
        </BaseCard>

        <!-- Tableau des ventes incluses -->
        <BaseCard>
          <template #header>
            <h3 class="text-lg font-semibold text-gray-900">Ventes incluses ({{ invoice.sales?.length || 0 }})</h3>
          </template>

          <div class="overflow-x-auto">
            <table class="w-full text-sm">
              <thead class="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th class="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">N° Vente</th>
                  <th class="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Date</th>
                  <th class="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase">Montant</th>
                  <th class="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody class="divide-y divide-gray-100">
                <tr v-for="sale in invoice.sales" :key="sale.id" class="hover:bg-gray-50 transition-colors">
                  <td class="px-4 py-3">
                    <span class="font-mono font-semibold text-primary cursor-pointer hover:underline"
                      @click="viewSale(sale)">
                      {{ sale.invoice_number }}
                    </span>
                  </td>
                  <td class="px-4 py-3 text-gray-600">{{ formatDateShort(sale.sale_date) }}</td>
                  <td class="px-4 py-3 text-right font-semibold text-gray-900">{{ formatCurrency(sale.total_incl_tax) }}</td>
                  <td class="px-4 py-3 text-center">
                    <button @click="viewSale(sale)" class="text-gray-400 hover:text-primary transition-colors" title="Voir la vente">
                      <i class="fa-solid fa-eye"></i>
                    </button>
                  </td>
                </tr>
              </tbody>
              <tfoot class="bg-gray-50 border-t-2 border-gray-200">
                <tr>
                  <td colspan="2" class="px-4 py-3 text-right font-semibold text-gray-700">Total facture</td>
                  <td class="px-4 py-3 text-right font-bold text-lg text-primary">{{ formatCurrency(invoice.total_amount) }}</td>
                  <td></td>
                </tr>
              </tfoot>
            </table>
          </div>
        </BaseCard>

      </div>

      <!-- Colonne droite: Résumé et Actions -->
      <div class="space-y-6">
        <!-- Résumé financier -->
        <BaseCard>
          <template #header>
            <h3 class="text-lg font-semibold text-gray-900">Résumé</h3>
          </template>

          <div class="space-y-4">
            <div class="flex justify-between items-center pb-3 border-b border-gray-100">
              <span class="text-sm text-gray-600">Montant total</span>
              <span class="text-lg font-bold text-gray-900">{{ formatCurrency(invoice.total_amount) }}</span>
            </div>
            <div class="flex justify-between items-center pb-3 border-b border-gray-100">
              <span class="text-sm text-gray-600">Montant payé</span>
              <span class="text-lg font-bold text-green-600">{{ formatCurrency(invoice.paid_amount) }}</span>
            </div>
            <div class="flex justify-between items-center">
              <span class="text-sm font-medium" :class="remainingAmount > 0 ? 'text-red-600' : 'text-gray-600'">
                Reste à payer
              </span>
              <span class="text-xl font-bold" :class="remainingAmount > 0 ? 'text-red-600' : 'text-green-600'">
                {{ formatCurrency(remainingAmount) }}
              </span>
            </div>
          </div>

          <!-- Barre de progression -->
          <div class="mt-4">
            <div class="flex justify-between text-xs text-gray-500 mb-1">
              <span>Progression</span>
              <span>{{ paymentProgress }}%</span>
            </div>
            <div class="w-full bg-gray-200 rounded-full h-2">
              <div class="h-2 rounded-full transition-all duration-300"
                :class="invoice.status === 'paid' ? 'bg-green-500' : 'bg-primary'"
                :style="{ width: paymentProgress + '%' }">
              </div>
            </div>
          </div>
        </BaseCard>

        <!-- Historique des paiements simplifié -->
        <BaseCard v-if="invoice.payments && invoice.payments.length > 0">
          <template #header>
            <div class="flex items-center justify-between w-full">
              <h3 class="text-lg font-semibold text-gray-900">Paiements</h3>
              <button
                @click="openPaymentHistoryModal"
                class="text-sm text-primary hover:text-primary-dark font-medium flex items-center gap-1"
              >
                Voir tout
                <i class="fa-solid fa-arrow-right text-xs"></i>
              </button>
            </div>
          </template>

          <div class="space-y-3">
            <!-- Afficher les 3 derniers paiements -->
            <div
              v-for="payment in invoice.payments.slice(0, 3)"
              :key="payment.id"
              class="flex items-center justify-between py-2 border-b border-gray-100 last:border-0"
            >
              <div class="flex items-center gap-3">
                <div class="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                  <i class="fa-solid fa-check text-green-600 text-sm"></i>
                </div>
                <div>
                  <p class="text-sm font-medium text-gray-900">{{ formatCurrency(payment.amount) }}</p>
                  <p class="text-xs text-gray-500">{{ formatDateShort(payment.payment_date) }}</p>
                </div>
              </div>
              <span class="text-xs font-medium text-gray-500 bg-gray-100 px-2 py-1 rounded">
                {{ getPaymentMethodName(payment.payment_type) }}
              </span>
            </div>

            <!-- Indicateur s'il y a plus de paiements -->
            <p v-if="invoice.payments.length > 3" class="text-xs text-center text-gray-500 pt-2">
              + {{ invoice.payments.length - 3 }} autre(s) paiement(s)
            </p>
          </div>
        </BaseCard>

        <!-- Aucun paiement -->
        <BaseCard v-else-if="invoice.status !== 'paid'">
          <div class="text-center py-6">
            <div class="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
              <i class="fa-solid fa-receipt text-gray-400"></i>
            </div>
            <p class="text-sm text-gray-500 mb-3">Aucun paiement enregistré</p>
            <BaseButton variant="primary" size="sm" @click="openPaymentModal">
              <template #icon-left>
                <i class="fa-solid fa-plus"></i>
              </template>
              Ajouter un paiement
            </BaseButton>
          </div>
        </BaseCard>

        <!-- Facture payée -->
        <BaseCard v-if="invoice.status === 'paid'" class="border-2 border-green-200 bg-green-50">
          <div class="text-center py-4">
            <div class="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
              <i class="fa-solid fa-check text-3xl text-green-600"></i>
            </div>
            <h3 class="text-lg font-semibold text-green-800 mb-1">Facture soldée</h3>
            <p class="text-sm text-green-600 mb-3">Cette facture a été entièrement payée</p>
            <button
              v-if="invoice.payments && invoice.payments.length > 0"
              @click="openPaymentHistoryModal"
              class="text-sm text-green-700 hover:text-green-800 font-medium flex items-center gap-1 mx-auto"
            >
              <i class="fa-solid fa-clock-rotate-left"></i>
              Voir l'historique des paiements
            </button>
          </div>
        </BaseCard>

        <!-- Actions -->
        <BaseCard v-if="canDelete">
          <template #header>
            <h3 class="text-lg font-semibold text-gray-900">Actions</h3>
          </template>

          <div class="space-y-2">
            <BaseButton variant="danger" block @click="confirmDeleteInvoice">
              <template #icon-left>
                <i class="fa-solid fa-trash"></i>
              </template>
              Supprimer la facture
            </BaseButton>
          </div>
        </BaseCard>
      </div>
    </div>

    <!-- Message d'erreur -->
    <BaseCard v-else-if="!loading && !invoice.id">
      <div class="flex flex-col items-center justify-center py-20 text-center">
        <div class="w-16 h-16 text-gray-300 mb-4">
          <i class="fa-solid fa-exclamation-circle text-6xl"></i>
        </div>
        <h2 class="text-xl font-semibold text-gray-900 mb-2">Facture introuvable</h2>
        <p class="text-gray-600 mb-6">La facture que vous recherchez n'existe pas.</p>
        <BaseButton variant="primary" @click="goBack">
          Retour
        </BaseButton>
      </div>
    </BaseCard>

    <!-- Modal de confirmation de suppression -->
    <Teleport to="body">
      <div v-if="showDeleteModal" class="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
        <div class="bg-white rounded-2xl shadow-xl max-w-md w-full p-6 animate-fade-in">
          <div class="text-center">
            <div class="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <i class="fa-solid fa-trash text-2xl text-red-600"></i>
            </div>
            <h3 class="text-xl font-bold text-gray-900 mb-2">{{ deleteModalTitle }}</h3>
            <p class="text-gray-600 mb-6">{{ deleteModalMessage }}</p>
            <div class="flex gap-3">
              <BaseButton variant="ghost" block @click="closeDeleteModal" :disabled="isDeleting">
                Annuler
              </BaseButton>
              <BaseButton variant="danger" block @click="executeDelete" :loading="isDeleting">
                Supprimer
              </BaseButton>
            </div>
          </div>
        </div>
      </div>
    </Teleport>

    <!-- Modal d'ajout de paiement -->
    <MakeSaleInvoicePaymentModal
      :show="showPaymentModal"
      :invoice-id="invoice.id"
      :invoice-number="invoice.invoice_number"
      :total-amount="invoice.total_amount"
      :paid-amount="invoice.paid_amount"
      @close="closePaymentModal"
      @payment-added="onPaymentAdded"
    />

    <!-- Modal d'historique des paiements -->
    <PaymentHistoryModal
      :show="showPaymentHistoryModal"
      :invoice-id="invoice.id"
      :invoice-number="invoice.invoice_number"
      :invoice-status="invoice.status"
      :payments="invoice.payments || []"
      :total-amount="invoice.total_amount"
      :paid-amount="invoice.paid_amount"
      :customer-name="invoice.customer?.name"
      :customer-phone="invoice.customer?.phone"
      @close="closePaymentHistoryModal"
      @payment-deleted="onPaymentDeleted"
    />
  </div>
</template>

<script>
import { defineComponent, ref, computed, onMounted, onUnmounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { saleInvoiceService } from '@/services'
import { useToastStore } from '@/store/modules/toastStore'
import BaseCard from '@/components/base/BaseCard.vue'
import BaseButton from '@/components/base/BaseButton.vue'
import MakeSaleInvoicePaymentModal from '@/components/saleInvoices/MakeSaleInvoicePaymentModal.vue'
import PaymentHistoryModal from '@/components/saleInvoices/PaymentHistoryModal.vue'

export default defineComponent({
  name: 'SaleInvoiceDetailsView',
  components: {
    BaseCard,
    BaseButton,
    MakeSaleInvoicePaymentModal,
    PaymentHistoryModal
  },
  setup() {
    const route = useRoute()
    const router = useRouter()
    const toastStore = useToastStore()

    const invoice = ref({})
    const loading = ref(true)
    const isDownloading = ref(false)
    const isDownloadingDetailed = ref(false)
    const showDownloadDropdown = ref(false)
    const isDeleting = ref(false)

    // Delete modal state
    const showDeleteModal = ref(false)
    const deleteModalTitle = ref('')
    const deleteModalMessage = ref('')
    const deleteAction = ref(null)

    // Payment modals state
    const showPaymentModal = ref(false)
    const showPaymentHistoryModal = ref(false)

    const remainingAmount = computed(() => {
      return (invoice.value.total_amount || 0) - (invoice.value.paid_amount || 0)
    })

    const paymentProgress = computed(() => {
      if (!invoice.value.total_amount) return 0
      return Math.round((invoice.value.paid_amount / invoice.value.total_amount) * 100)
    })

    const isOverdue = computed(() => {
      if (!invoice.value.due_date || invoice.value.status === 'paid') return false
      return new Date(invoice.value.due_date) < new Date()
    })

    const canDelete = computed(() => {
      return invoice.value.status === 'pending' && (!invoice.value.payments || invoice.value.payments.length === 0)
    })

    const openPaymentModal = () => {
      showPaymentModal.value = true
    }

    const closePaymentModal = () => {
      showPaymentModal.value = false
    }

    const openPaymentHistoryModal = () => {
      showPaymentHistoryModal.value = true
    }

    const closePaymentHistoryModal = () => {
      showPaymentHistoryModal.value = false
    }

    const onPaymentAdded = async () => {
      await loadInvoiceDetails()
    }

    const onPaymentDeleted = async () => {
      await loadInvoiceDetails()
    }

    const loadInvoiceDetails = async () => {
      const invoiceId = route.params.id
      if (!invoiceId) {
        router.push({ name: 'customers' })
        return
      }

      try {
        loading.value = true
        const response = await saleInvoiceService.getSaleInvoice(invoiceId)
        invoice.value = response.data.data || response.data
      } catch (err) {
        console.error('Erreur:', err)
        invoice.value = {}
      } finally {
        loading.value = false
      }
    }

    const formatCurrency = (amount) => {
      return new Intl.NumberFormat('fr-FR', {
        style: 'currency',
        currency: 'XOF',
        minimumFractionDigits: 0
      }).format(amount || 0)
    }

    const formatDate = (dateString) => {
      if (!dateString) return ''
      const date = new Date(dateString)
      return date.toLocaleDateString('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    }

    const formatDateShort = (dateString) => {
      if (!dateString) return ''
      const date = new Date(dateString)
      return date.toLocaleDateString('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      })
    }

    const getStatusLabel = (status) => {
      const labels = {
        'pending': 'En attente',
        'partial': 'Partielle',
        'paid': 'Payée'
      }
      return labels[status] || status
    }

    const getStatusBadgeClass = (status) => {
      const classes = {
        'pending': 'bg-yellow-100 text-yellow-800',
        'partial': 'bg-blue-100 text-blue-800',
        'paid': 'bg-green-100 text-green-800'
      }
      return classes[status] || 'bg-gray-100 text-gray-800'
    }

    const getPaymentMethodName = (type) => {
      const types = {
        'cash': 'Espèces',
        'card': 'Carte',
        'mobile_money': 'Mobile Money',
        'bank_transfer': 'Virement',
        'check': 'Chèque'
      }
      return types[type] || type
    }

    const goBack = () => {
      router.back()
    }

    const viewSale = (sale) => {
      router.push({ name: 'sale-details', params: { id: sale.id } })
    }

    const viewCustomer = () => {
      if (invoice.value.customer?.id) {
        router.push({ name: 'customer-detail', params: { id: invoice.value.customer.id } })
      }
    }

    const toggleDownloadDropdown = () => {
      showDownloadDropdown.value = !showDownloadDropdown.value
    }

    const downloadInvoice = async (type) => {
      if (isDownloading.value || isDownloadingDetailed.value) return

      try {
        if (type === 'grouped') {
          isDownloading.value = true
          showDownloadDropdown.value = false
          await saleInvoiceService.downloadInvoice(invoice.value.id)
        } else if (type === 'detailed') {
          isDownloadingDetailed.value = true
          showDownloadDropdown.value = false
          await saleInvoiceService.downloadInvoiceDetailed(invoice.value.id)
        }
      } catch (error) {
        console.error('Erreur lors du téléchargement de la facture:', error)
        toastStore.addToast({
          type: 'error',
          message: 'Erreur lors du téléchargement de la facture',
          duration: 4000
        })
      } finally {
        isDownloading.value = false
        isDownloadingDetailed.value = false
      }
    }

    // Gérer le clic en dehors du dropdown pour le fermer
    const handleClickOutside = (event) => {
      const dropdown = event.target.closest('.invoice-download-dropdown-container')
      if (!dropdown && showDownloadDropdown.value) {
        showDownloadDropdown.value = false
      }
    }

    const confirmDeletePayment = (payment) => {
      deleteModalTitle.value = 'Supprimer le paiement ?'
      deleteModalMessage.value = `Êtes-vous sûr de vouloir supprimer ce paiement de ${formatCurrency(payment.amount)} ?`
      deleteAction.value = () => deletePayment(payment.id)
      showDeleteModal.value = true
    }

    const confirmDeleteInvoice = () => {
      deleteModalTitle.value = 'Supprimer la facture ?'
      deleteModalMessage.value = `Êtes-vous sûr de vouloir supprimer la facture ${invoice.value.invoice_number} ? Les ventes associées seront dissociées.`
      deleteAction.value = deleteInvoice
      showDeleteModal.value = true
    }

    const closeDeleteModal = () => {
      showDeleteModal.value = false
      deleteAction.value = null
    }

    const executeDelete = async () => {
      if (deleteAction.value) {
        await deleteAction.value()
      }
    }

    const deletePayment = async (paymentId) => {
      try {
        isDeleting.value = true
        await saleInvoiceService.deleteInvoicePayment(invoice.value.id, paymentId)

        toastStore.addToast({
          type: 'success',
          message: 'Paiement supprimé',
          duration: 3000
        })

        closeDeleteModal()
        await loadInvoiceDetails()
      } catch (error) {
        console.error('Erreur:', error)
        toastStore.addToast({
          type: 'error',
          message: 'Erreur lors de la suppression',
          duration: 4000
        })
      } finally {
        isDeleting.value = false
      }
    }

    const deleteInvoice = async () => {
      try {
        isDeleting.value = true
        await saleInvoiceService.deleteSaleInvoice(invoice.value.id)

        toastStore.addToast({
          type: 'success',
          message: 'Facture supprimée',
          duration: 3000
        })

        closeDeleteModal()
        router.push({ name: 'customers' })
      } catch (error) {
        console.error('Erreur:', error)
        toastStore.addToast({
          type: 'error',
          message: 'Erreur lors de la suppression',
          duration: 4000
        })
      } finally {
        isDeleting.value = false
      }
    }

    onMounted(() => {
      loadInvoiceDetails()
      document.addEventListener('click', handleClickOutside)
    })

    onUnmounted(() => {
      document.removeEventListener('click', handleClickOutside)
    })

    return {
      invoice,
      loading,
      isDownloading,
      isDownloadingDetailed,
      showDownloadDropdown,
      isDeleting,
      showDeleteModal,
      deleteModalTitle,
      deleteModalMessage,
      showPaymentModal,
      showPaymentHistoryModal,
      remainingAmount,
      paymentProgress,
      isOverdue,
      canDelete,
      formatCurrency,
      formatDate,
      formatDateShort,
      getStatusLabel,
      getStatusBadgeClass,
      getPaymentMethodName,
      goBack,
      viewSale,
      viewCustomer,
      toggleDownloadDropdown,
      downloadInvoice,
      openPaymentModal,
      closePaymentModal,
      openPaymentHistoryModal,
      closePaymentHistoryModal,
      onPaymentAdded,
      onPaymentDeleted,
      confirmDeletePayment,
      confirmDeleteInvoice,
      closeDeleteModal,
      executeDelete
    }
  }
})
</script>

<style scoped>
.animate-fade-in {
  animation: fadeIn 0.2s ease-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: scale(0.95);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

.invoice-download-dropdown-container {
  position: relative;
}

.invoice-download-dropdown {
  position: absolute;
  top: calc(100% + 0.5rem);
  right: 0;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
  min-width: 200px;
  z-index: 100;
  overflow: hidden;
}

.dropdown-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  width: 100%;
  padding: 0.75rem 1rem;
  border: none;
  background: white;
  color: #111827;
  font-size: 0.875rem;
  text-align: left;
  cursor: pointer;
  transition: background-color 0.2s;
}

.dropdown-item:hover:not(:disabled) {
  background: #f9fafb;
}

.dropdown-item:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.dropdown-item i {
  width: 16px;
  color: #0A5C6B;
}

.fa-chevron-down {
  transition: transform 0.2s;
  font-size: 0.75rem;
}

.fa-chevron-down.rotated {
  transform: rotate(180deg);
}
</style>
